import { isNumber } from 'lodash-es';

export type DurationRoundingStyle = null | 'round' | 'ceil' | 'floor';

export enum RestrictDuration {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    Minimum = 'minimum',
    // eslint-disable-next-line @typescript-eslint/naming-convention
    Maximum = 'maximum',
}

export interface IDurationRounding {
    style: DurationRoundingStyle;
    to: number;
    midpoint_seconds: number;
    minimum_seconds: number;
    restrict_duration: RestrictDuration;
}

const STYLE_CEIL = 'ceil';
const STYLE_ROUND = 'round';
const STYLE_FLOOR = 'floor';
const VALID_STYLES = [null, STYLE_ROUND, STYLE_CEIL, STYLE_FLOOR];

/**
 * @param style
 */
function checkStyleIsValid(style: DurationRoundingStyle) {
    if (style !== undefined && style !== null && VALID_STYLES.indexOf(style) === -1) {
        console.error(style);

        throw new Error('Rounding style must be one of: ' + VALID_STYLES.join(', '));
    }
}

export class DurationRounding {
    constructor(
        public style: DurationRoundingStyle,
        public roundToMinutes: number,
        public restrictDuration: RestrictDuration = null,
        public midpointSeconds: number = null,
        public minimumSeconds: number = null,
    ) {
        checkStyleIsValid(style);
    }

    withMidpointMinutes(minutes: number) {
        return this.withMidpointSeconds(minutes * 60);
    }

    withMidpointSeconds(seconds: number) {
        this.midpointSeconds = seconds;
        return this;
    }

    /**
     * @param minutes
     * @param scheduledDurationSeconds
     * @returns
     */
    roundMinutes(minutes: number, scheduledDurationSeconds: number = null) {
        const roundedSeconds = this.roundSeconds(
            minutes * 60, // Convert to seconds
            scheduledDurationSeconds,
        );

        return roundedSeconds / 60; // Convert back to minutes
    }

    /**
     * @param seconds
     * @param scheduledDuration
     * @returns
     */
    roundSeconds(seconds: number, scheduledDuration: number = null) {
        if (this.minimumSeconds && seconds < this.minimumSeconds) {
            return this.minimumSeconds;
        }

        if (this.restrictDuration && !isNumber(scheduledDuration)) {
            throw new Error('scheduledDuration is expected when restrictDuration is set');
        }

        const roundToSeconds = this.roundToMinutes * 60;
        const remainder = roundToSeconds ? seconds % roundToSeconds : 0;

        if (this.style === STYLE_ROUND) {
            if (isPresent(this.midpointSeconds)) {
                if (remainder >= this.midpointSeconds) {
                    seconds = Math.ceil(seconds / roundToSeconds) * roundToSeconds;
                } else {
                    seconds = Math.floor(seconds / roundToSeconds) * roundToSeconds;
                }
            } else {
                seconds = Math.round(seconds / roundToSeconds) * roundToSeconds;
            }
        } else if (this.style === STYLE_CEIL) {
            if (isPresent(this.midpointSeconds)) {
                if (remainder >= this.midpointSeconds) {
                    seconds = Math.ceil(seconds / roundToSeconds) * roundToSeconds;
                }
            } else {
                seconds = Math.ceil(seconds / roundToSeconds) * roundToSeconds;
            }
        } else if (this.style === STYLE_FLOOR) {
            if (isPresent(this.midpointSeconds)) {
                if (remainder >= this.midpointSeconds) {
                    seconds = Math.floor(seconds / roundToSeconds) * roundToSeconds;
                }
            } else {
                seconds = Math.floor(seconds / roundToSeconds) * roundToSeconds;
            }
        }

        if (this.restrictDuration === RestrictDuration.Minimum) {
            return Math.max(scheduledDuration, seconds);
        }

        if (this.restrictDuration === RestrictDuration.Maximum) {
            return Math.min(scheduledDuration, seconds);
        }

        return seconds;
    }

    /**
     * @returns
     */
    toObject(): IDurationRounding {
        return {
            style: this.style,
            to: this.roundToMinutes,
            restrict_duration: this.restrictDuration,
            midpoint_seconds: this.midpointSeconds,
            minimum_seconds: this.minimumSeconds,
        };
    }

    /**
     * @returns
     */
    toJSON() {
        return JSON.stringify(this.toObject());
    }

    /**
     * @param roundToMinutes
     * @param minimumScheduled
     * @returns
     */
    static ceil(roundToMinutes, minimumScheduled = null): DurationRounding {
        return new DurationRounding(STYLE_CEIL, roundToMinutes, minimumScheduled);
    }

    /**
     * @param roundToMinutes
     * @param minimumScheduled
     * @returns
     */
    static round(roundToMinutes, minimumScheduled = null): DurationRounding {
        return new DurationRounding(STYLE_ROUND, roundToMinutes, minimumScheduled);
    }

    /**
     * @param {Number} roundToMinutes
     * @param {Boolean} minimumScheduled
     * @returns {DurationRounding}
     */
    static floor(roundToMinutes, minimumScheduled = null): DurationRounding {
        return new DurationRounding(STYLE_FLOOR, roundToMinutes, minimumScheduled);
    }

    /**
     * @param {Object} obj
     * @returns {DurationRounding}
     */
    static fromObject(obj: DurationRounding | IDurationRounding) {
        if (obj instanceof DurationRounding) {
            return obj;
        }

        if (!obj) {
            return new DurationRounding(null, 0);
        }

        return new DurationRounding(
            obj.style,
            obj.to || 0,
            obj.restrict_duration,
            obj.midpoint_seconds,
        );
    }

    /**
     * @returns
     */
    static newDefault(): DurationRounding {
        return new DurationRounding(null, 0);
    }
}

function isPresent(...elements) {
    for (const el of elements) {
        if (el === undefined || el === null) {
            return false;
        }
    }

    return true;
}
